import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../../utils/config';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Users, 
  Bed,
  Bath,
  Wifi,
  Car,
  Loader2,
  Search,
  Building2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Check,
  X,
  Filter,
  Archive,
  Home,
  Plus,
  Pencil,
  FileDown
} from 'lucide-react';
import { useIsExternalTenant } from '../../hooks/useIsExternalTenant';
import PropertyFormModal from './PropertyFormModal';
import HomeSmartPropertyReportDialog from './HomeSmartPropertyReportDialog';

const ImmobiliGestiti = () => {
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCity, setFilterCity] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const isExternal = useIsExternalTenant();
  const [showPropertyForm, setShowPropertyForm] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [reportProperty, setReportProperty] = useState(null);

  useEffect(() => {
    fetchProperties();
    // Segna come "visti" gli immobili per azzerare il badge
    localStorage.setItem('hw_last_viewed_properties', new Date().toISOString());
  }, []);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('hw_token');
      // Use my-properties endpoint to get only managed properties
      const response = await axios.get(`${API}/api/responsible/my-properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data || []);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  // Separate active and archived properties
  const activeProperties = useMemo(() => 
    properties.filter(p => !p.archived),
    [properties]
  );
  
  const archivedProperties = useMemo(() => 
    properties.filter(p => p.archived),
    [properties]
  );

  // Get unique cities for filter (from current view)
  const cities = useMemo(() => {
    const currentList = showArchived ? archivedProperties : activeProperties;
    const uniqueCities = [...new Set(currentList.map(p => p.city).filter(Boolean))];
    return uniqueCities.sort();
  }, [activeProperties, archivedProperties, showArchived]);

  // Filter and sort properties
  const filteredAndSortedProperties = useMemo(() => {
    // Start with active or archived based on toggle
    let result = showArchived ? [...archivedProperties] : [...activeProperties];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(prop => 
        prop.name?.toLowerCase().includes(term) ||
        prop.address?.toLowerCase().includes(term) ||
        prop.city?.toLowerCase().includes(term) ||
        prop.property_code?.toLowerCase().includes(term)
      );
    }

    // Apply status filter (active/inactive within the current view)
    if (filterStatus !== 'all') {
      result = result.filter(prop => 
        filterStatus === 'active' ? prop.active !== false : prop.active === false
      );
    }

    // Apply city filter
    if (filterCity !== 'all') {
      result = result.filter(prop => prop.city === filterCity);
    }

    // Apply sorting
    result.sort((a, b) => {
      let aVal = a[sortColumn] || '';
      let bVal = b[sortColumn] || '';
      
      // Handle numeric columns
      if (['max_guests', 'bedrooms', 'bathrooms'].includes(sortColumn)) {
        aVal = parseInt(aVal) || 0;
        bVal = parseInt(bVal) || 0;
      } else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [activeProperties, archivedProperties, showArchived, searchTerm, filterStatus, filterCity, sortColumn, sortDirection]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1 text-blue-600" />
      : <ArrowDown className="h-4 w-4 ml-1 text-blue-600" />;
  };

  // Reset city filter when switching between active/archived
  const handleToggleArchived = (value) => {
    setShowArchived(value);
    setFilterCity('all');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-3 text-slate-600">Caricamento immobili...</span>
      </div>
    );
  }

  return (
    <div className="p-4 pt-14 md:pt-6 md:p-6 space-y-4" data-testid="immobili-gestiti">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800">
            {showArchived ? 'Immobili Archiviati' : 'Immobili Gestiti'}
          </h1>
          <p className="text-slate-500 mt-1">
            {filteredAndSortedProperties.length} di {showArchived ? archivedProperties.length : activeProperties.length} immobili
          </p>
        </div>
        
        {/* Toggle Tabs */}
        <div className="flex w-full flex-wrap items-center gap-2 md:w-auto">
          {isExternal && (
            <Button
              size="sm"
              className="w-full bg-blue-700 text-white hover:bg-blue-800 sm:w-auto"
              onClick={() => { setEditingProperty(null); setShowPropertyForm(true); }}
              data-testid="new-property-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nuovo Immobile
            </Button>
          )}
          <div className="grid w-full min-w-0 grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1 sm:w-auto">
          <Button
            variant={!showArchived ? "default" : "ghost"}
            size="sm"
            onClick={() => handleToggleArchived(false)}
            className={`min-w-0 px-2 ${!showArchived ? "bg-white shadow-sm" : "hover:bg-slate-200"}`}
            data-testid="active-properties-tab"
          >
            <Home className="h-4 w-4 mr-2" />
            Attivi ({activeProperties.length})
          </Button>
          <Button
            variant={showArchived ? "default" : "ghost"}
            size="sm"
            onClick={() => handleToggleArchived(true)}
            className={`min-w-0 px-2 ${showArchived ? "bg-white shadow-sm" : "hover:bg-slate-200"}`}
            data-testid="archived-properties-tab"
          >
            <Archive className="h-4 w-4 mr-2" />
            Archiviati ({archivedProperties.length})
          </Button>
          </div>
        </div>
      </div>

      {/* Filters Row */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input 
                placeholder="Cerca per nome, indirizzo, città o codice..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-properties"
              />
            </div>

            {/* Status Filter */}
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full md:w-40" data-testid="filter-status">
                <Filter className="h-4 w-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="active">Attivi</SelectItem>
                <SelectItem value="inactive">Inattivi</SelectItem>
              </SelectContent>
            </Select>

            {/* City Filter */}
            <Select value={filterCity} onValueChange={setFilterCity}>
              <SelectTrigger className="w-full md:w-48" data-testid="filter-city">
                <SelectValue placeholder="Città" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le città</SelectItem>
                {cities.map(city => (
                  <SelectItem key={city} value={city}>{city}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Archived Notice */}
      {showArchived && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
          <Archive className="h-5 w-5 text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-800">
            Stai visualizzando gli immobili archiviati. Questi immobili non sono più attivi nel tuo portafoglio gestito.
          </p>
        </div>
      )}

      {/* Table */}
      {filteredAndSortedProperties.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-slate-500">
            {showArchived ? (
              <Archive className="h-16 w-16 text-slate-300 mb-4" />
            ) : (
              <Building2 className="h-16 w-16 text-slate-300 mb-4" />
            )}
            <p className="text-xl font-medium">
              {showArchived ? 'Nessun immobile archiviato' : 'Nessun immobile trovato'}
            </p>
            <p className="text-sm mt-2">
              {showArchived ? 'Non hai immobili archiviati' : 'Prova a modificare i filtri'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className={showArchived ? 'border-amber-200' : ''}>
          <CardContent className="p-0">
            <div className="overflow-auto max-h-[calc(100vh-320px)]">
              <table className="w-full" data-testid="properties-table">
                <thead className={`${showArchived ? 'bg-amber-50' : 'bg-slate-50'} border-b sticky top-0 z-10 shadow-sm`}>
                  <tr>
                    <th 
                      className={`px-4 py-3 text-left text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 ${showArchived ? 'bg-amber-50' : 'bg-slate-50'}`}
                      onClick={() => handleSort('property_code')}
                    >
                      <div className="flex items-center">
                        Codice <SortIcon column="property_code" />
                      </div>
                    </th>
                    <th 
                      className={`px-4 py-3 text-left text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 ${showArchived ? 'bg-amber-50' : 'bg-slate-50'}`}
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center">
                        Nome <SortIcon column="name" />
                      </div>
                    </th>
                    <th 
                      className={`px-4 py-3 text-left text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 ${showArchived ? 'bg-amber-50' : 'bg-slate-50'}`}
                      onClick={() => handleSort('city')}
                    >
                      <div className="flex items-center">
                        Città <SortIcon column="city" />
                      </div>
                    </th>
                    <th 
                      className={`px-4 py-3 text-left text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 hidden md:table-cell ${showArchived ? 'bg-amber-50' : 'bg-slate-50'}`}
                      onClick={() => handleSort('address')}
                    >
                      <div className="flex items-center">
                        Indirizzo <SortIcon column="address" />
                      </div>
                    </th>
                    <th 
                      className={`px-4 py-3 text-center text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 ${showArchived ? 'bg-amber-50' : 'bg-slate-50'}`}
                      onClick={() => handleSort('max_guests')}
                    >
                      <div className="flex items-center justify-center">
                        <Users className="h-4 w-4 mr-1" />
                        <SortIcon column="max_guests" />
                      </div>
                    </th>
                    <th 
                      className={`px-4 py-3 text-center text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 hidden lg:table-cell ${showArchived ? 'bg-amber-50' : 'bg-slate-50'}`}
                      onClick={() => handleSort('bedrooms')}
                    >
                      <div className="flex items-center justify-center">
                        <Bed className="h-4 w-4 mr-1" />
                        <SortIcon column="bedrooms" />
                      </div>
                    </th>
                    <th 
                      className={`px-4 py-3 text-center text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 hidden lg:table-cell ${showArchived ? 'bg-amber-50' : 'bg-slate-50'}`}
                      onClick={() => handleSort('bathrooms')}
                    >
                      <div className="flex items-center justify-center">
                        <Bath className="h-4 w-4 mr-1" />
                        <SortIcon column="bathrooms" />
                      </div>
                    </th>
                    <th className={`px-4 py-3 text-center text-sm font-semibold text-slate-700 hidden md:table-cell ${showArchived ? 'bg-amber-50' : 'bg-slate-50'}`}>
                      WiFi
                    </th>
                    <th className={`px-4 py-3 text-center text-sm font-semibold text-slate-700 hidden md:table-cell ${showArchived ? 'bg-amber-50' : 'bg-slate-50'}`}>
                      Parking
                    </th>
                    <th 
                      className={`px-4 py-3 text-center text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 ${showArchived ? 'bg-amber-50' : 'bg-slate-50'}`}
                      onClick={() => handleSort('active')}
                    >
                      <div className="flex items-center justify-center">
                        Stato <SortIcon column="active" />
                      </div>
                    </th>
                    {isExternal && (
                      <th className={`px-4 py-3 text-center text-sm font-semibold text-slate-700 ${showArchived ? 'bg-amber-50' : 'bg-slate-50'}`}>
                        Azioni
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredAndSortedProperties.map((property, idx) => (
                    <tr 
                      key={property.id || idx}
                      className={`hover:bg-slate-50 ${showArchived ? 'opacity-75' : ''}`}
                      data-testid={`property-row-${property.id || idx}`}
                    >
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm font-medium text-blue-600">
                          {property.property_code || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-slate-800 notranslate" translate="no">
                          {property.name || 'N/A'}
                        </span>
                        {isExternal && (
                          <Badge variant="outline" className="ml-2 text-xs" data-testid={`property-role-badge-${property.id || idx}`}>
                            {property.home_smart_property_role === 'gestore' ? 'Gestore' : property.home_smart_property_role === 'proprietario' ? 'Proprietario' : 'Ruolo da indicare'}
                          </Badge>
                        )}
                        {showArchived && (
                          <Badge variant="outline" className="ml-2 text-xs bg-amber-100 text-amber-700 border-amber-200">
                            Archiviato
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {property.city || '-'}
                      </td>
                      <td className="px-4 py-3 text-slate-600 hidden md:table-cell">
                        <span className="line-clamp-1">{property.address || '-'}</span>
                      </td>
                      <td className="px-4 py-3 text-center text-slate-600">
                        {property.max_guests || '-'}
                      </td>
                      <td className="px-4 py-3 text-center text-slate-600 hidden lg:table-cell">
                        {property.bedrooms || '-'}
                      </td>
                      <td className="px-4 py-3 text-center text-slate-600 hidden lg:table-cell">
                        {property.bathrooms || '-'}
                      </td>
                      <td className="px-4 py-3 text-center hidden md:table-cell">
                        {property.has_wifi !== false ? (
                          <Check className="h-4 w-4 text-green-500 mx-auto" />
                        ) : (
                          <X className="h-4 w-4 text-slate-300 mx-auto" />
                        )}
                      </td>
                      <td className="px-4 py-3 text-center hidden md:table-cell">
                        {property.has_parking ? (
                          <Check className="h-4 w-4 text-green-500 mx-auto" />
                        ) : (
                          <X className="h-4 w-4 text-slate-300 mx-auto" />
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge 
                          variant={property.active !== false ? 'default' : 'secondary'}
                          className={property.active !== false ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}
                        >
                          {property.active !== false ? 'Attivo' : 'Inattivo'}
                        </Badge>
                      </td>
                      {isExternal && (
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button variant="ghost" size="sm" title="Scarica report PDF" onClick={() => setReportProperty(property)} data-testid={`property-report-btn-${property.id || idx}`}>
                              <FileDown className="h-4 w-4 text-emerald-700" />
                            </Button>
                            <Button variant="ghost" size="sm" title="Modifica immobile" onClick={() => { setEditingProperty(property); setShowPropertyForm(true); }} data-testid={`edit-property-btn-${property.id || idx}`}>
                              <Pencil className="h-4 w-4 text-blue-600" />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <PropertyFormModal
        open={showPropertyForm}
        onClose={() => setShowPropertyForm(false)}
        property={editingProperty}
        onSaved={fetchProperties}
      />
      <HomeSmartPropertyReportDialog
        open={!!reportProperty}
        onClose={() => setReportProperty(null)}
        property={reportProperty}
      />
    </div>
  );
};

export default ImmobiliGestiti;
